import { RowSelectionState } from '@tanstack/react-table';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { SelectionStatus } from 'ui-kit';

import { AssetClass, Filter } from '__generated__/globalTypes';
import { FormValues } from '../pages/portfolio/CreatePoolForm';
import { useCreatePool } from '../pages/portfolio/hooks/useCreatePool';
import { clearPoolCandidate } from './sessionPoolCandidate';

interface Args {
  tapeFilter: Filter[];
  gqlAssetClass: AssetClass;
}

/**
 * Shared submit hook for the two tape-driven pool surfaces (post-PERSISTED
 * overlay action and the loan-datatable banner). Wraps the canonical
 * `useCreatePool` so the asyncPoolCreation feature-flag gate, the legacy
 * CREATE_LISTING fallback, refetchQueries, and the sync/async result
 * handling all stay consistent with the rest of the app.
 *
 * Behaviour: on success → clear sessionStorage candidate, surface a single
 * success toast (sync vs async), navigate to Pool Manager. On failure →
 * surface a single error toast and re-throw so the host modal's Formik
 * onSubmit doesn't fall through to closeModal() and the user keeps their
 * form input.
 */
export const useCreatePoolFromTape = ({ tapeFilter, gqlAssetClass }: Args) => {
  const navigate = useNavigate();
  const { createPool, createPoolLoading } = useCreatePool();

  const submit = async (formValues: FormValues) => {
    // Selection-state args are unused when overrideFilters is set, but the
    // existing useCreatePool signature requires them. Pass the no-selection
    // sentinel so the tape branch is unambiguous.
    const emptySelection: RowSelectionState = {};

    let result;
    try {
      result = await createPool(
        formValues,
        gqlAssetClass,
        [],
        emptySelection,
        SelectionStatus.None,
        tapeFilter,
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error(`Error creating pool: ${msg}`);
      // Re-throw so the modal stays mounted and the user keeps their input.
      throw err;
    }

    clearPoolCandidate();
    if (result.type === 'async') {
      toast.success(
        `Pool "${formValues.poolName}" is being created. Track progress in the Jobs dropdown.`,
      );
    } else {
      toast.success(`Pool "${formValues.poolName}" created.`);
    }
    navigate('/portfolio/pool-manager');
  };

  return { submit, loading: createPoolLoading };
};
